'use client'

import { cn } from '../../../lib/cn'

export interface ParticlesProps {
  className?: string
}

// Fixed particle positions and properties for consistency
const PARTICLES = [
  // Top region
  { x: 12, y: 8, size: 2, delay: 0, duration: 20 },
  { x: 28, y: 15, size: 3, delay: 2, duration: 25 },
  { x: 45, y: 6, size: 2, delay: 4, duration: 22 },
  { x: 68, y: 12, size: 4, delay: 1, duration: 28 },
  { x: 85, y: 5, size: 2, delay: 3, duration: 24 },
  { x: 95, y: 18, size: 2, delay: 6, duration: 23 },
  { x: 38, y: 22, size: 3, delay: 0, duration: 26 },
  { x: 5, y: 3, size: 2, delay: 4, duration: 21 },
  { x: 55, y: 18, size: 2, delay: 1, duration: 24 },
  // Middle-upper region
  { x: 8, y: 35, size: 3, delay: 5, duration: 26 },
  { x: 22, y: 42, size: 2, delay: 0, duration: 21 },
  { x: 52, y: 38, size: 3, delay: 2, duration: 27 },
  { x: 75, y: 32, size: 2, delay: 4, duration: 23 },
  { x: 92, y: 40, size: 3, delay: 1, duration: 25 },
  { x: 3, y: 28, size: 2, delay: 3, duration: 22 },
  { x: 62, y: 45, size: 2, delay: 5, duration: 24 },
  { x: 42, y: 30, size: 3, delay: 0, duration: 26 },
  { x: 88, y: 25, size: 2, delay: 2, duration: 23 },
  // Middle-lower region
  { x: 15, y: 55, size: 2, delay: 2, duration: 21 },
  { x: 32, y: 58, size: 3, delay: 4, duration: 26 },
  { x: 48, y: 52, size: 2, delay: 1, duration: 23 },
  { x: 82, y: 60, size: 3, delay: 0, duration: 27 },
  { x: 95, y: 55, size: 2, delay: 3, duration: 22 },
  { x: 5, y: 48, size: 2, delay: 5, duration: 25 },
  { x: 68, y: 50, size: 3, delay: 1, duration: 24 },
  // Bottom region
  { x: 15, y: 65, size: 4, delay: 3, duration: 29 },
  { x: 35, y: 72, size: 2, delay: 5, duration: 22 },
  { x: 58, y: 68, size: 3, delay: 0, duration: 26 },
  { x: 78, y: 75, size: 2, delay: 2, duration: 24 },
  { x: 5, y: 88, size: 3, delay: 4, duration: 27 },
  { x: 42, y: 92, size: 2, delay: 1, duration: 21 },
  { x: 65, y: 85, size: 4, delay: 3, duration: 28 },
  { x: 88, y: 95, size: 2, delay: 5, duration: 23 },
  { x: 25, y: 82, size: 2, delay: 0, duration: 25 },
  { x: 72, y: 90, size: 3, delay: 2, duration: 24 },
  { x: 48, y: 78, size: 2, delay: 4, duration: 23 },
  { x: 92, y: 72, size: 3, delay: 1, duration: 26 },
  { x: 18, y: 95, size: 2, delay: 3, duration: 22 },
  { x: 55, y: 62, size: 3, delay: 0, duration: 25 },
  { x: 98, y: 85, size: 2, delay: 5, duration: 24 },
]

/**
 * Particles - Animated floating dots background
 *
 * Creates a subtle field of drifting particles using CSS animations.
 * Respects prefers-reduced-motion by disabling animations.
 *
 * Fixed settings (not configurable):
 * - 18 particles with varied sizes (2-4px)
 * - Slow drift animation (20-29s duration)
 * - Uses muted-foreground color at low opacity
 *
 * @example
 * ```tsx
 * <Particles />
 * ```
 */
export function Particles({ className }: ParticlesProps = {}) {
  return (
    <div
      className={cn('pointer-events-none absolute inset-0 z-0 overflow-hidden', className)}
    >
      <style>{`
        @keyframes particle-drift {
          0%, 100% {
            transform: translate(0, 0);
          }
          25% {
            transform: translate(10px, -15px);
          }
          50% {
            transform: translate(-5px, 10px);
          }
          75% {
            transform: translate(-15px, -5px);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .particle {
            animation: none !important;
          }
        }
      `}</style>

      {PARTICLES.map((particle, i) => (
        <div
          key={i}
          className="particle absolute rounded-full bg-muted-foreground opacity-[0.15] dark:opacity-[0.20]"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            animation: `particle-drift ${particle.duration}s ease-in-out infinite`,
            animationDelay: `${particle.delay}s`,
          }}
        />
      ))}
    </div>
  )
}
